export interface Course {
  id: string;
  title: string;
  description: string;
  level: string;
  image: string;
}

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  image: string;
}

export interface Module {
  id: string;
  code: string;
  title: string;
  description: string;
}

export interface SubModule {
  id: string;
  title: string;
  moduleId: string;
  description: string;
  icon?: string;
}

export interface ModuleCourse {
  id: string;
  title: string;
  subModuleId: string;
  description: string;
  hasVideo: boolean;
  icon?: string;
  free?: boolean;
}

export interface CourseProgress {
  courseId: string;
  completed: boolean;
  lastAccessedAt: string;
}

export interface CourseContent {
  id: string;
  title: string;
  type: 'markdown' | 'video';
  content: string;
  videoUrl?: string;
  moduleId: string;
  subModuleId: string;
}

export interface SubModuleProgress {
  id: string;
  progress: number;
}

export interface QuizQuestion {
  id: string;
  _id?: string;
  moduleId: string;
  module_code?: string;
  course_id?: string | null;
  question: string;
  options: string[];
  choices?: {
    A: string;
    B: string;
    C: string;
    D: string;
  };
  correctAnswer: number;
  correct_answer?: string;
  explanation: string;
  difficulty: 'facile' | 'moyen' | 'difficile';
}

export interface ProgressiveQuizQuestion {
  _id: string;
  question: string;
  choices: {
    A: string;
    B: string;
    C: string;
    D: string;
  };
  correct_answer: string;
  explanation: string;
  module_code: string;
  course_id: string | null;
  difficulty: 'facile' | 'moyen' | 'difficile';
}

export interface QuizSubmission {
  mode: 'progressive' | 'free';
  difficulty: 'easy' | 'medium' | 'difficult' | 'mix';
  answers: {
    question_id: string;
    selected: string;
  }[];
  module_codes: string[];
}

export interface QuizSubmissionResult {
  score: number;
  correct: number;
  total: number;
  details: {
    question_id: string;
    selected_answer: string;
    correct_answer: string;
    is_correct: boolean;
    difficulty: string;
  }[];
}

export interface QuizResult {
  totalQuestions: number;
  correctAnswers: number;
  moduleResults: {
    moduleId: string;
    correct: number;
    total: number;
  }[];
  date?: string;
}

export interface QuizStatistics {
  id: string;
  date: string;
  moduleResults: {
    moduleId: string;
    moduleName: string;
    correct: number;
    total: number;
    percentage: number;
  }[];
  totalCorrect: number;
  totalQuestions: number;
  overallPercentage: number;
}

export interface QuizStats {
  total_qcm: number;
  today_qcm: number;
  success_rate: number;
  average_score: number;
  by_difficulty: {
    easy: number;
    medium: number;
    difficult: number;
  };
  by_module: {
    [moduleCode: string]: {
      total: number;
      success_100: number;
      average: number;
    };
  };
  unlocked_modes: string[];
  score_history: {
    date: string;
    score: number;
  }[];
  qcm_summary: {
    success_100_by_difficulty: {
      facile: number;
      moyen: number;
      difficile: number;
    };
    overall_success_rate: number;
  };
  success_questions_by_module: {
    [moduleCode: string]: {
      [difficulty: string]: {
        correct: number;
        total: number;
        available: number;
      };
    };
  };
  mode_progress: {
    medium: {
      current: number;
      required: number;
      unlocked: boolean;
    };
    free: {
      current: number;
      required: number;
      unlocked: boolean;
    };
  };
}

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  avatar: string;
  role: 'user' | 'admin';
  joinedAt: string;
  objective?: string;
  experience: number;
  level: string;
  unlocked_modes?: string[];
  subscription?: string;
  subscribed_at?: string;
  subscribed_until?: string;
  unsubscribed_at?: string;
}

export interface ModuleProgress {
  moduleId: string;
  moduleName: string;
  completedLessons: number;
  totalLessons: number;
  lastAccessedAt: string;
  progress: number;
}

export interface PricingPlan {
  id: string;
  name: string;
  price: number;
  description: string;
  features: string[];
  recommended?: boolean;
  yearlyPrice?: number;
}

export interface StripeCheckoutSession {
  payment_required: boolean;
  invoice_url?: string;
  checkout_url?: string;
  amount_due?: number;
  currency?: string;
  message?: string;
}

export interface UpgradePreview {
  amount_due_now: number;
  next_payment_amount: number;
  currency: string;
  next_payment_date: string | null;
}

export interface FreeQuizCount {
  remaining: number;
  limit: number;
  is_limited: boolean;
}

export enum SubscriptionPlan {
  DETERMINED_MONTHLY = "DETERMINED_MONTHLY",
  DETERMINED_ANNUALLY = "DETERMINED_ANNUALLY",
  MOTIVATED_MONTHLY = "MOTIVATED_MONTHLY",
  MOTIVATED_ANNUALLY = "MOTIVATED_ANNUALLY",
  SHY_MONTHLY = "SHY_MONTHLY",
  SHY_ANNUALLY = "SHY_ANNUALLY"
}

export interface SubscriptionRequest {
  plan: SubscriptionPlan;
  success_url: string;
  cancel_url: string;
  successUrl: string;
  cancelUrl: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: {
    id: string;
    email: string;
    name: string;
    picture: string;
    objective?: string;
    experience?: number;
    level?: string;
    subscription?: string;
    subscribed_at?: string;
    subscribed_until?: string;
  };
}

export type UserLevel = {
  name: string;
  minExperience: number;
  icon: string;
};

export interface Comment {
  id: string;
  date: string;
  user_id: string;
  course_id: string;
  content: string;
  seen: boolean;
}

export interface AdminComment extends Comment {
  user_name: string;
  course_title: string;
}

export interface AdminCourseComments {
  course_id: string;
  title: string;
  comments: Comment[];
}

export interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  is_first_connection: boolean;
  objective: string;
  subscription?: string;
  subscribed_until?: string;
  subscribed_at?: string;
  subscription_id?: string;
  stripe_customer_id?: string;
  unsubscribed_at?: string;
  experience: number;
  level: string;
  modules_progress?: any;
  unlocked_modes: string[];
  role: string;
}

export interface UserProgress {
  [courseId: string]: number;
}

export interface CourseTitles {
  [courseId: string]: string;
}

export interface AdminCourse {
  id: string;
  title: string;
  content: string;
  type: 'markdown' | 'video';
  video_url?: string;
  module_code: string;
  module_title: string;
  sub_module_title: string;
}

export interface AdminQuizQuestion {
  id: string;
  question: string;
  choices: {
    A: string;
    B: string;
    C: string;
    D?: string;
  };
  correct_answer: string;
  explanation: string;
  difficulty: 'facile' | 'moyen' | 'difficile';
  module_code: string;
  course_id: string;
}

export interface AdminQuestionModule {
  module_code: string;
  questions: AdminQuizQuestion[];
}